import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { RelevancyCardProps } from '../DynamicCardComponent';
import { useRelevancyFeed } from '../model';
import { RelevancyCardFormat } from '../types';

import type { EditorialContent } from '@cobalt/utilities';
import { mapEditorialContent } from '@cobalt/utilities';
import { useVideoLinkRule } from '@hooks/useLinkRule';
import PictureHelper from '@shared/PictureHelper';
import { useTranslateFn } from '@shared/Translate';
import { useMediaAssetService } from '@shared/VideoPlayer/hooks/useMediaAssetService';

dayjs.extend(duration);

const imgFormat = ['1440@w1', '1200@w1', '960@w1', '768@w1', '440@w1', '0@w1'];
const styleForLink = {
  display: 'block',
  height: '100%',
  width: '100%',
  position: 'absolute',
  zIndex: 9,
} as CSSProperties;

const VideoElement = ({ video }) => {
  const href = useVideoLinkRule(video.id);

  const videoDuration = useMemo(() => {
    const duration = video.duration ?? video.mediaDuration;
    return dayjs.duration(duration, 's').format('mm:ss');
  }, [video.duration, video.mediaDuration]);

  return (
    <a className='card-link' href={href} title={video.title}>
      <article className='article with-picture video'>
        <figure className='article--img-wrap'>
          <span className='icon-play'> </span>
          <span className='video-duration'>{videoDuration}</span>
          <PictureHelper formats={imgFormat} className='article--img' imageUrl={video.imageUrl} title={video.title} />
          <span className='article--title'>{video.title}</span>
        </figure>
      </article>
    </a>
  );
};

export const HighlightGroupComponent = ({ item, cardFormat }: RelevancyCardProps) => {
  const t = useTranslateFn();
  const { toggleCardHidden } = useRelevancyFeed();
  const [videos, setVideos] = useState<EditorialContent[]>([]);

  const videoSection = useMemo(() => item.type.split('_')?.[0]?.toLowerCase(), [item.type]);
  const wideCard = useMemo(() => cardFormat === RelevancyCardFormat.WIDE, [cardFormat]);

  const mappedVideos: EditorialContent[] = useMemo(() => item?.teaser?.map(mapEditorialContent) ?? [], [item?.teaser]);

  const initCarousel = useCallback(
    (newVideos: EditorialContent[]) => {
      if (videos.length === newVideos.length) return;

      setVideos(newVideos);
    },
    [videos.length],
  );

  const availableVideos = useMediaAssetService(mappedVideos);

  useEffect(() => {
    toggleCardHidden(item, !availableVideos.length);
    if (!availableVideos.length) {
      return;
    }

    initCarousel(availableVideos);
  }, [availableVideos, initCarousel, item, toggleCardHidden]);

  const background = useMemo(() => {
    if (cardFormat !== 'square') return {};
    return {
      backgroundImage: videos.length ? `url(${videos[0].imageUrl.replace('_w1', '_s1')})` : '',
    };
  }, [cardFormat, videos]);

  return (
    <div
      className={`card carousel-video ${videoSection} card-format-${cardFormat}`}
      data-testid={`carousel-video--${videoSection}`}
    >
      {cardFormat === 'square' && <a href={`video/${videoSection}/`} style={styleForLink} title={videoSection} />}
      <div className='wrap' style={background}>
        {cardFormat === 'square' && <span className='icon-play'> </span>}
        {cardFormat === 'square' && <div className='triangle'> </div>}
        {videoSection === 'highlights' && <h3>{t('watch_highlights')}</h3>}
        {videoSection === 'skills' && (
          <>
            <h3>{t('skills')}</h3>
            {cardFormat !== 'square' && cardFormat !== 'wide' && (
              <h4>{t(`subtitle-skills-${item.competitionCode}`)}</h4>
            )}
          </>
        )}
        {cardFormat !== 'square' && !!videos.length && (
          <Swiper
            modules={[Navigation]}
            slidesPerView={wideCard ? 2.1 : 1.1}
            spaceBetween={10}
            navigation
            className={`${videos.length % 2 === 1 ? 'odd' : ''}`}
          >
            {videos.map((video) => (
              <SwiperSlide key={`item_${video.id}`} className='item'>
                <VideoElement video={video} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        <a className='all-highlights' href={`video/${videoSection}/`}>
          {t(`watch-all-${videoSection}`)}
        </a>
      </div>
    </div>
  );
};

export default HighlightGroupComponent;
